// Copyright (c) 2026 Finerty Data Ltd. All rights reserved.
//
// This file is part of proprietary software owned by Finerty Data Ltd.
// It is confidential and may not be copied, distributed, modified, or used,
// in whole or in part, without the prior written consent of Finerty Data Ltd.
//
// Author: Ahmed Hosny

import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { proposalOverviewApi } from '../../api/proposalOverviewApi';
import {
  proposalTypeLabel,
  type ProposalFilterOption,
  type ProposalOverviewDisplayStatus,
  type ProposalOverviewItem,
} from '../../types/proposal';

const PAGE_SIZE = 10;

/**
 * Advisor dashboard card: a paginated, filterable feed of the advisor's
 * proposals across their relationships. Mirrors the Request Overview card.
 */
export const ProposalOverviewCard: React.FC = () => {
  const navigate = useNavigate();
  const [items, setItems] = useState<ProposalOverviewItem[]>([]);
  const [totalPages, setTotalPages] = useState(0);
  const [page, setPage] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  const [proposalType, setProposalType] = useState('');
  const [displayStatus, setDisplayStatus] = useState('');
  const [typeOptions, setTypeOptions] = useState<ProposalFilterOption[]>([]);
  const [statusOptions, setStatusOptions] = useState<ProposalFilterOption[]>([]);

  useEffect(() => {
    proposalOverviewApi.getTypeOptions().then(setTypeOptions).catch(() => setTypeOptions([]));
    proposalOverviewApi.getStatusOptions().then(setStatusOptions).catch(() => setStatusOptions([]));
  }, []);

  useEffect(() => {
    setLoading(true);
    proposalOverviewApi
      .list({
        search: search || undefined,
        proposalType: proposalType || undefined,
        displayStatus: (displayStatus || undefined) as ProposalOverviewDisplayStatus | undefined,
        page,
        size: PAGE_SIZE,
      })
      .then((res) => {
        setItems(res.content);
        setTotalPages(res.totalPages);
      })
      .catch((e: unknown) => setError(e instanceof Error ? e.message : 'Failed to load proposals'))
      .finally(() => setLoading(false));
  }, [search, proposalType, displayStatus, page]);

  const onFilterChange = (setter: (v: string) => void) => (e: React.ChangeEvent<HTMLSelectElement>) => {
    setPage(0);
    setter(e.target.value);
  };

  return (
    <div data-testid="proposal-overview-card">
      <h2>Proposals Overview</h2>
      <div style={{ display: 'flex', gap: 8, marginBottom: 12 }}>
        <input
          data-testid="overview-search"
          placeholder="Search"
          value={search}
          onChange={(e) => {
            setPage(0);
            setSearch(e.target.value);
          }}
        />
        <select data-testid="overview-type-filter" value={proposalType} onChange={onFilterChange(setProposalType)}>
          <option value="">All types</option>
          {typeOptions.map((o) => (
            <option key={o.value} value={o.value}>{o.label}</option>
          ))}
        </select>
        <select data-testid="overview-status-filter" value={displayStatus} onChange={onFilterChange(setDisplayStatus)}>
          <option value="">All statuses</option>
          {statusOptions.map((o) => (
            <option key={o.value} value={o.value}>{o.label}</option>
          ))}
        </select>
      </div>

      {loading && <div data-testid="overview-loading">Loading…</div>}
      {error && <div data-testid="overview-error" style={{ color: '#b91c1c' }}>{error}</div>}

      {!loading && !error && items.length === 0 && (
        <div data-testid="overview-empty">No proposals match.</div>
      )}

      {!loading && !error && items.length > 0 && (
        <table data-testid="overview-table" style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead>
            <tr>
              <th style={{ textAlign: 'left' }}>Type</th>
              <th style={{ textAlign: 'left' }}>Number</th>
              <th style={{ textAlign: 'left' }}>Client</th>
              <th style={{ textAlign: 'left' }}>Status</th>
            </tr>
          </thead>
          <tbody>
            {items.map((item) => (
              <tr
                key={item.proposalId}
                data-testid={`overview-row-${item.proposalId}`}
                onClick={() => navigate(`/relationships/${item.profileId}/proposals/${item.proposalId}`)}
                style={{ cursor: 'pointer', borderTop: '1px solid #e5e7eb' }}
              >
                <td>{proposalTypeLabel(item.proposalType)}</td>
                <td>{item.proposalNumber}</td>
                <td>{item.targetClientName ?? '—'}</td>
                <td>{item.displayStatus}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {totalPages > 1 && (
        <div style={{ display: 'flex', gap: 8, marginTop: 8 }}>
          <button data-testid="overview-prev" disabled={page === 0} onClick={() => setPage((p) => p - 1)}>
            Previous
          </button>
          <span data-testid="overview-page">{page + 1} / {totalPages}</span>
          <button
            data-testid="overview-next"
            disabled={page >= totalPages - 1}
            onClick={() => setPage((p) => p + 1)}
          >
            Next
          </button>
        </div>
      )}
    </div>
  );
};

export default ProposalOverviewCard;
